import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BookOpenTextIcon, DotsThreeIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';

import {
  AGENT_MODES,
  createPlaybook,
  deletePlaybook,
  getPlaybook,
  updatePlaybook,
  usePlaybooks,
  type AgentMode,
  type Playbook,
} from '@/api-models/playbooks';
import { useAuth } from '@/contexts/auth-context';
import { useSpace } from '@/contexts/space-context';
import { Badge } from '@/ui/Badge';
import { Button, IconButton } from '@/ui/button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui/DropdownMenu';
import { Input } from '@/ui/Input';
import { Modal } from '@/ui/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';
import { SegmentedControl } from '@/ui/SegmentedControl';
import { Spinner } from '@/ui/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';
import { Textarea } from '@/ui/Textarea';
import { formatTimeAgo } from '@/utils/format';

type Scope = 'personal' | 'team';

const MODE_LABELS: Record<AgentMode, string> = {
  agent: 'Agent',
  plan: 'Plan',
  ask: 'Ask',
};

/**
 * Settings → Playbooks.
 *
 * A library of preset prompts that guide the agent for common tasks. Playbooks
 * live at either a personal or a team level — both map onto a real team (every
 * user has a personal team), so a single segmented toggle switches the scope
 * between the user's personal space and the team the settings page is scoped to.
 */
export function PlaybooksSection() {
  const { me } = useAuth();
  const { activeTeam } = useSpace();

  const teamSpace = activeTeam && !activeTeam.is_personal ? activeTeam : null;
  const [scope, setScope] = useState<Scope>(teamSpace ? 'team' : 'personal');

  // Switching spaces changes which scopes exist; follow the space.
  useEffect(() => {
    setScope(teamSpace ? 'team' : 'personal');
  }, [teamSpace]);

  const effectiveTeamId = scope === 'team' && teamSpace ? teamSpace.id : me.personalTeamId;
  const { playbooks, mutate, isLoading } = usePlaybooks(effectiveTeamId);

  const [editor, setEditor] = useState<{ playbook: Playbook | null } | null>(null);

  async function handleDelete(playbook: Playbook) {
    try {
      await deletePlaybook(effectiveTeamId, playbook.id);
      await mutate();
      toast.success(`Deleted "${playbook.name}"`);
    } catch {
      toast.error('Could not delete playbook');
    }
  }

  return (
    <div className="flex flex-col gap-7">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex flex-col">
          <h1 className="page-title">Playbooks</h1>
          <p className="page-subtitle">
            Reusable prompts that tell agents how to handle common tasks, like migrating a codebase or shipping a
            feature.
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size={9}
          onClick={() => setEditor({ playbook: null })}
          iconLeft={<PlusIcon size={16} weight="bold" />}
        >
          New playbook
        </Button>
      </div>

      {/* Scope controls — the Team scope only exists inside a team space. */}
      {teamSpace && (
        <div className="flex items-center gap-3">
          <SegmentedControl<Scope>
            value={scope}
            onChange={setScope}
            ariaLabel="Playbook scope"
            options={[
              { value: 'personal', label: 'Personal' },
              { value: 'team', label: 'Team' },
            ]}
          />
          <span className="text-xs text-fg-tertiary">
            {scope === 'team'
              ? `Shared with everyone in ${teamSpace.name}.`
              : 'Only you can see and use these playbooks.'}
          </span>
        </div>
      )}

      {/* List */}
      <div className="overflow-hidden rounded-xl border border-line bg-surface-1">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell className="w-44">Shortcut</TableHeaderCell>
              <TableHeaderCell className="w-28">Mode</TableHeaderCell>
              <TableHeaderCell className="w-40">Updated</TableHeaderCell>
              <TableHeaderCell className="w-12" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && playbooks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-fg-tertiary">
                  <span className="inline-flex items-center gap-2">
                    <Spinner size={4} /> Loading playbooks…
                  </span>
                </TableCell>
              </TableRow>
            ) : playbooks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-fg-tertiary">
                  No playbooks yet. Create one to give your agents a head start on a recurring task.
                </TableCell>
              </TableRow>
            ) : (
              playbooks.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <button
                      type="button"
                      className="flex min-w-0 items-center gap-2.5 text-left"
                      onClick={() => setEditor({ playbook: p })}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-3 text-fg-secondary">
                        <BookOpenTextIcon size={16} />
                      </span>
                      <span className="truncate text-sm font-semibold text-fg">{p.name}</span>
                    </button>
                  </TableCell>
                  <TableCell>
                    {p.macro ? (
                      <code className="rounded bg-surface-3 px-1.5 py-0.5 text-xs text-fg-secondary">{p.macro}</code>
                    ) : (
                      <span className="text-fg-tertiary">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="neutral" size="md">
                      {MODE_LABELS[p.agent_mode]}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium text-fg-secondary">{formatTimeAgo(p.updated_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <PlaybookMenu onEdit={() => setEditor({ playbook: p })} onDelete={() => handleDelete(p)} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editor && (
        <PlaybookEditorModal
          teamId={effectiveTeamId}
          playbook={editor.playbook}
          onClose={() => setEditor(null)}
          onSaved={mutate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function PlaybookMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton size={8} aria-label="Playbook actions">
          <DotsThreeIcon size={18} weight="bold" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <PencilSimpleIcon size={15} />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem destructive onSelect={onDelete}>
          <TrashIcon size={15} />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PlaybookEditorModal({
  teamId,
  playbook,
  onClose,
  onSaved,
}: {
  teamId: string;
  playbook: Playbook | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = playbook !== null;

  const [name, setName] = useState(playbook?.name ?? '');
  const [macro, setMacro] = useState(playbook?.macro ?? '');
  const [agentMode, setAgentMode] = useState<AgentMode>(playbook?.agent_mode ?? 'agent');
  const [content, setContent] = useState('');
  const [loadingContent, setLoadingContent] = useState(isEdit);
  const [busy, setBusy] = useState(false);

  // The markdown body is offloaded, so fetch it when editing an existing playbook.
  useEffect(() => {
    if (!playbook) return;
    let cancelled = false;
    setLoadingContent(true);
    getPlaybook(teamId, playbook.id)
      .then((detail) => {
        if (!cancelled) setContent(detail.content);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load playbook content');
      })
      .finally(() => {
        if (!cancelled) setLoadingContent(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId, playbook]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    const body = {
      name: name.trim(),
      content,
      macro: macro.trim() || null,
      agent_mode: agentMode,
    };
    try {
      if (isEdit && playbook) {
        await updatePlaybook(teamId, playbook.id, body);
        toast.success('Playbook updated');
      } else {
        await createPlaybook(teamId, body);
        toast.success('Playbook created');
      }
      onSaved();
      onClose();
    } catch {
      toast.error(isEdit ? 'Could not update playbook' : 'Could not create playbook');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit playbook' : 'New playbook'}
      description="Give the agent reusable instructions for a common task."
      maxWidth="max-w-2xl"
    >
      <form onSubmit={submit} className="flex flex-col gap-4 px-6 pb-6">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="pb-name" className="text-xs font-medium text-fg">
            Name
          </label>
          <Input
            id="pb-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Migrate from TypeScript to Rust"
            autoFocus
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pb-macro" className="text-xs font-medium text-fg">
              Shortcut <span className="font-normal text-fg-tertiary">(optional)</span>
            </label>
            <Input
              id="pb-macro"
              value={macro}
              onChange={(e) => setMacro(e.target.value)}
              placeholder="/migrate-ts-rust"
            />
            <span className="text-2xs leading-3.5 text-fg-tertiary">Type this in a prompt to run the playbook.</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-fg">Agent mode</span>
            <Select value={agentMode} onValueChange={(v) => setAgentMode(v as AgentMode)}>
              <SelectTrigger aria-label="Agent mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start">
                {AGENT_MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value} description={m.description}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-2xs leading-3.5 text-fg-tertiary">How much the agent may do on its own.</span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="pb-content" className="text-xs font-medium text-fg">
            Instructions <span className="font-normal text-fg-tertiary">(Markdown)</span>
          </label>
          <Textarea
            id="pb-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={'# Goal\n\nDescribe what the agent should do, step by step…'}
            disabled={loadingContent}
            className="h-72 font-mono text-xs leading-5"
          />
        </div>

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" size={9} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size={9} disabled={!name.trim() || loadingContent} isLoading={busy}>
            {isEdit ? 'Save changes' : 'Create playbook'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
